import React, { useEffect, useRef, useState } from 'react';
import { FormOptionItem, FormOptionItem2 } from '../../../utils/interfaces/generalInterfaces';
import { twMerge } from 'tailwind-merge';
import { COLORS } from '../../../utils/colors';
import { ChevronDown, ChevronUp, Search, X } from 'react-feather';

const ChevronDownIcon = ({ disabled }: { disabled: boolean }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke={disabled ? '#9CA3AF' : '#6B7280'}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

type GroupedOptionItem = FormOptionItem & { groupId: string };

export const DillDropDownFilter = ({
  items,
  inputFieldStyles,
  isMulti = false,
  disabled = false,
  closeMenu = true,
  placeholder = 'Select',
  onSelectChange = () => {},
  onMultiSelectChange = () => {},
  multiValue,
  errorText = '',
  valueStyle = '',
  customViewElement = null,
  showSelectedItems = false,
  showMinimalDesign = false,
  numberOfItemsToShow = 1,
  itemWidth = 200,
  inputHeight = 36,
}: {
  items: {
    id: string;
    label: string;
    value: FormOptionItem2[];
    isDisabled?: boolean;
    isSearchable?: boolean;
    customContent?: React.ReactNode;
    activeLabel?: string;
  }[];
  closeMenu?: boolean;
  isMulti?: boolean;
  disabled?: boolean;
  placeholder?: string;
  inputFieldStyles?: any;
  errorText?: string;
  valueStyle?: string;
  onSelectChange?: (newValue: FormOptionItem) => void;
  onMultiSelectChange?: (selected: { [key: string]: GroupedOptionItem[] }) => void;
  multiValue?: { [key: string]: string[] } | null;
  customViewElement?: any;
  showSelectedItems?: boolean;
  showMinimalDesign?: boolean;
  numberOfItemsToShow?: number;
  itemWidth?: number;
  inputHeight?: number;
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [selectedValue, setSelectedValue] = useState<FormOptionItem | null>();
  const [selectedMultiValues, setSelectedMultiValues] = useState<{
    [key: string]: GroupedOptionItem[];
  }>({});
  // Track collapsed state per group — all expanded by default
  const [collapsedGroups, setCollapsedGroups] = useState<{ [key: string]: boolean }>({});
  // Track search query per group
  const [searchQueries, setSearchQueries] = useState<{ [key: string]: string }>({});

  const inputRef = useRef<HTMLButtonElement>(null);
  const customViewInputRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (multiValue && isMulti) {
      const newSelections: { [key: string]: GroupedOptionItem[] } = {};
      items.forEach((group) => {
        const selected = group.value
          .filter((opt) => multiValue[group.id]?.includes(opt.value))
          .map((opt) => ({ ...opt, groupId: group.id }));
        newSelections[group.id] = selected;
      });
      setSelectedMultiValues(newSelections);
    } else {
      if (isMulti) {
        setSelectedMultiValues({});
        setSelectedValue(null);
      }
    }
  }, [multiValue, isMulti, items]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, [closeMenu]);

  const handleInputClick = () => setShowMenu((prev) => !prev);

  const toggleGroupCollapse = (groupId: string) => {
    setCollapsedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const handleSearchChange = (groupId: string, query: string) => {
    setSearchQueries((prev) => ({ ...prev, [groupId]: query }));
  };

  const getFilteredOptions = (group: (typeof items)[0]) => {
    const query = searchQueries[group.id]?.toLowerCase() || '';
    if (!query) return group.value;
    return group.value.filter((opt) => opt.label.toLowerCase().includes(query));
  };

  const removeOption = (option: GroupedOptionItem, groupId: string) =>
    selectedMultiValues[groupId]?.filter((o) => o.value !== option.value) || [];

  const onTagRemove = (e: any, option: GroupedOptionItem, groupId: string) => {
    e.stopPropagation();
    const newValue = removeOption(option, groupId);
    const updated = { ...selectedMultiValues, [groupId]: newValue };
    setSelectedMultiValues(updated);
    onMultiSelectChange(updated);
  };

  const getDisplay = () => {
    const activeLabels = items.filter((item) => item.activeLabel).map((item) => item.activeLabel!);

    if (!selectedValue && !showSelectedItems) {
      if (activeLabels.length === 0) {
        return <span className="text-gray-400 text-xs text-left w-full">{placeholder}</span>;
      }
      return (
        <div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
          {activeLabels.map((label) => (
            <span
              key={label}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-50 border border-blue-200 text-[11px] font-medium text-blue-700 shrink-0 max-w-[160px]">
              <span className="truncate">{label}</span>
            </span>
          ))}
        </div>
      );
    }

    if (isMulti && showSelectedItems) {
      const allSelections = Object.values(selectedMultiValues).flat();

      if (allSelections.length === 0 && activeLabels.length === 0) {
        return <span className="text-gray-400 text-xs text-left w-full">{placeholder}</span>;
      }

      if (showMinimalDesign) {
        const allItems = [
          ...allSelections.map((o) => ({ type: 'option' as const, option: o })),
          ...activeLabels.map((l) => ({ type: 'label' as const, label: l })),
        ];
        const overflow = allItems.length > numberOfItemsToShow;
        return (
          <div className="flex items-center gap-1.5 min-w-0">
            <div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
              {allItems.slice(0, numberOfItemsToShow).map((item, i) =>
                item.type === 'option' ? (
                  <span
                    key={item.option.value}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-50 border border-blue-200 text-[11px] font-medium text-blue-700 shrink-0 max-w-[140px]">
                    <span className="truncate">{item.option.label}</span>
                    <button
                      type="button"
                      onClick={(e) => onTagRemove(e, item.option, item.option.groupId)}
                      className="text-blue-400 hover:text-blue-700 cursor-pointer bg-transparent border-none leading-none p-0 shrink-0">
                      <X size={11} />
                    </button>
                  </span>
                ) : (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-50 border border-blue-200 text-[11px] font-medium text-blue-700 shrink-0 max-w-[160px]">
                    <span className="truncate">{item.label}</span>
                  </span>
                )
              )}
            </div>
            {overflow && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 border border-gray-200 text-[11px] font-medium text-gray-600 shrink-0">
                +{allItems.length - numberOfItemsToShow}
              </span>
            )}
          </div>
        );
      }

      return (
        <div className="flex flex-wrap gap-1.5">
          {allSelections.map((option) => (
            <span
              key={option.value}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-50 border border-blue-200 text-[11px] font-medium text-blue-700">
              <span className="truncate max-w-[120px]">{option.label}</span>
              <button
                type="button"
                onClick={(e) => onTagRemove(e, option, option.groupId)}
                className="text-blue-400 hover:text-blue-700 cursor-pointer bg-transparent border-none leading-none p-0 shrink-0">
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      );
    }

    return <span className="text-gray-900 truncate">{selectedValue?.label || placeholder}</span>;
  };

  const onItemClick = (option: FormOptionItem, groupId: string) => {
    if (isMulti) {
      let newValue: GroupedOptionItem[];
      if (selectedMultiValues[groupId]?.findIndex((o) => o.value === option.value) >= 0) {
        newValue = removeOption({ ...option, groupId }, groupId);
      } else {
        newValue = [...(selectedMultiValues[groupId] || []), { ...option, groupId }];
      }
      const updated = { ...selectedMultiValues, [groupId]: newValue };
      setSelectedMultiValues(updated);
      onMultiSelectChange(updated);
    } else {
      setSelectedValue(option);
      onSelectChange(option);
    }

    if (closeMenu) setShowMenu((prev) => !prev);
  };

  const isSelected = (option: FormOptionItem, groupId: string) => {
    if (isMulti)
      return selectedMultiValues[groupId]?.some((o) => o.value === option.value) || false;
    if (!selectedValue) return false;
    return selectedValue.value === option.value;
  };

  return (
    <div className="w-full flex flex-col relative" ref={containerRef}>
      <div
        className={twMerge(
          `w-full border rounded-lg bg-white transition-all outline-none`,
          errorText
            ? 'border-red-400 focus:border-red-400 focus:ring-2 focus:ring-red-50'
            : showMenu
            ? 'border-[#3762FB] ring-2 ring-blue-50'
            : 'border-gray-200 hover:border-gray-400',
          disabled ? 'bg-gray-50 border-gray-200 cursor-not-allowed' : 'cursor-pointer'
        )}
        style={{ minHeight: `${inputHeight}px`, ...inputFieldStyles }}>
        {!customViewElement && (
          <button
            disabled={disabled}
            onClick={handleInputClick}
            className="relative flex items-center justify-between w-full px-3 min-w-0 gap-2 py-1 text-left"
            style={{ minHeight: `${inputHeight}px` }}
            ref={inputRef}>
            <div
              className={twMerge(
                'flex-1 min-w-0 text-xs',
                isMulti && showSelectedItems ? '' : 'truncate',
                disabled ? 'text-gray-400' : 'text-gray-900',
                valueStyle
              )}>
              {getDisplay()}
            </div>
            <span
              className={twMerge(
                'text-gray-400 transition-transform duration-200 shrink-0 self-center',
                showMenu && 'rotate-180'
              )}>
              <ChevronDownIcon disabled={disabled} />
            </span>
          </button>
        )}

        {customViewElement && (
          <div
            ref={customViewInputRef}
            className={
              disabled
                ? `disabled-dropdown-selected-value ${valueStyle}`
                : `dropdown-selected-value ${valueStyle}`
            }
            onClick={handleInputClick}>
            {customViewElement}
          </div>
        )}

        {showMenu && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
            <div className="max-h-60 overflow-y-auto py-1">
              {items.map((item, index) => {
                const isCollapsed = !!collapsedGroups[item.id];
                const filteredOptions = getFilteredOptions(item);

                return (
                  <div
                    key={index + 'option-index'}
                    className="border-b border-gray-100 last:border-b-0">
                    {/* Group header — clickable to collapse/expand */}
                    {item.label && (
                      <div
                        className="px-3 py-2 flex items-center justify-between cursor-pointer select-none hover:bg-gray-50 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleGroupCollapse(item.id);
                        }}>
                        <span className="font-semibold text-xs text-gray-700">{item.label}</span>
                        {isCollapsed ? (
                          <ChevronDown size={14} color={COLORS.GREY_500} />
                        ) : (
                          <ChevronUp size={14} color={COLORS.GREY_500} />
                        )}
                      </div>
                    )}

                    {/* Search input — only shown when isSearchable and group is not collapsed */}
                    {item.isSearchable && !isCollapsed && (
                      <div className="px-3 pb-2" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center border border-gray-200 rounded-lg px-2 gap-2 bg-gray-50 hover:bg-white hover:border-gray-300 transition-colors">
                          <Search size={14} color={COLORS.GREY_500} />
                          <input
                            type="text"
                            className="text-xs outline-none flex-1 placeholder-gray-400 bg-transparent py-1.5"
                            placeholder="Search..."
                            value={searchQueries[item.id] || ''}
                            onChange={(e) => handleSearchChange(item.id, e.target.value)}
                          />
                          {searchQueries[item.id] && (
                            <button
                              type="button"
                              className="text-gray-400 hover:text-gray-600 p-0.5"
                              onClick={() => handleSearchChange(item.id, '')}>
                              <X size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Custom content — rendered instead of options list when provided */}
                    {item.customContent && !isCollapsed && (
                      <div className="px-3 pb-3" onClick={(e) => e.stopPropagation()}>
                        {item.customContent}
                      </div>
                    )}

                    {/* Options list — hidden when collapsed */}
                    {!item.customContent && !isCollapsed && (
                      <>
                        {filteredOptions.length > 0 ? (
                          filteredOptions.map((option) => (
                            <button
                              type="button"
                              key={option.value}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (item?.value?.[0].isDisabled === true) return;
                                onItemClick(option, item.id);
                              }}
                              className={`w-full flex items-center justify-between px-3 py-2 text-xs text-left transition-colors cursor-pointer border-none
                                ${
                                  isSelected(option, item.id)
                                    ? 'bg-blue-50 text-blue-700 font-medium'
                                    : 'bg-white text-gray-700 hover:bg-gray-50'
                                }`}>
                              {option.labelInElement ? option.labelInElement : option.label}
                              {isSelected(option, item.id) && !option.labelInElement && (
                                <svg
                                  width="13"
                                  height="13"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="text-blue-600 shrink-0">
                                  <path d="M20 6L9 17l-5-5" />
                                </svg>
                              )}
                            </button>
                          ))
                        ) : (
                          <p className="text-xs text-gray-400 text-center py-4 px-3">
                            No results found
                          </p>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
